import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../../../../components/UIToolkit/Inputs/SystemDropDown";
import type { Job, NurseRole } from "../../../../../../utils/models";
import { RolePickerDialog } from "./RolePickerDialog";

/**
 * Guard for the "Don't sync" dropdown option. We use the empty string (not null) because
 * MUI's <Select> coerces a `null` value to "" internally — round-tripping a null breaks the
 * controlled-component contract. The component maps this sentinel back to `null` before
 * surfacing the change to the parent.
 */
export const DONT_SYNC_VALUE = "__dont_sync__";

export type JobPickerChange = { kind: "dontSync" } | { kind: "job"; jobId: string; role?: NurseRole };

interface JobPickerCellProps {
	roleString: string;
	jobs: Job[];
	/** Currently selected job (null when DONT_SYNC, undefined when orphan with no mapping yet). */
	selectedJob: Job | null | undefined;
	/** True when `position === Position.DontSync`. */
	isDontSync: boolean;
	/** True when the row has no cert mapping at all. */
	isOrphan: boolean;
	/** Existing role on the row (used when reassigning a job for an already-mapped row, no dialog). */
	currentRole: NurseRole | null;
	onChange: (change: JobPickerChange) => void;
	dataTestId?: string;
}

/**
 * Per-role-string job picker rendered in the HrisRawEmployees pane (and reusable elsewhere).
 *
 * Behavior:
 * - Choosing the DONT_SYNC option fires a `{ kind: "dontSync" }` change immediately (no dialog).
 * - Choosing a Job from a row that's currently DONT_SYNC, an orphan, or otherwise carries no role
 *   opens the role picker first; the change is fired only after the admin confirms the dialog.
 * - Choosing a Job from a row that already has a role reuses it and fires the change immediately.
 */
export const JobPickerCell: FC<JobPickerCellProps> = ({ roleString, jobs, selectedJob, isDontSync, isOrphan, currentRole, onChange, dataTestId }) => {
	const { t } = useTranslation();
	const [dialogPendingJobId, setDialogPendingJobId] = useState<string | null>(null);

	const items = useMemo(
		() => [
			{ value: DONT_SYNC_VALUE, label: t("hris.mapping.dontSync"), selectedLabel: t("hris.mapping.dontSync") },
			...jobs
				.sort((j1, j2) => j1.displayName.localeCompare(j2.displayName))
				.map((j) => ({ value: j.id, label: j.displayName ?? j.name, selectedLabel: j.displayName ?? j.name })),
		],
		[jobs, t],
	);

	const selectedItem: string | undefined = isDontSync ? DONT_SYNC_VALUE : (selectedJob?.id ?? undefined);

	const handleChange = (value?: string) => {
		if (value === undefined) return;
		if (value === DONT_SYNC_VALUE) {
			onChange({ kind: "dontSync" });
			return;
		}
		// Picking a job — ask for the role only when the row cannot supply one itself.
		if (isDontSync || isOrphan || !currentRole) {
			setDialogPendingJobId(value);
			return;
		}
		onChange({ kind: "job", jobId: value });
	};

	const handleDialogConfirm = (role: NurseRole) => {
		if (!dialogPendingJobId) return;
		onChange({ kind: "job", jobId: dialogPendingJobId, role });
		setDialogPendingJobId(null);
	};

	return (
		<>
			<SystemDropdown<string>
				items={items}
				selectedItem={selectedItem}
				onChange={handleChange}
				formControlSx={{ minWidth: 220 }}
				data-testid={dataTestId ?? `hris-mapping-job-picker-${roleString}`}
			/>
			{dialogPendingJobId !== null && <RolePickerDialog open onConfirm={handleDialogConfirm} onClose={() => setDialogPendingJobId(null)} />}
		</>
	);
};
